import { useCallback, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';

import { gridBooleanCell, gridNumberCell, dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { useLeadSource } from '@shared/hooks/useLeadSource';

import { ViewHeightWrapper } from '@styles/CommonStyles';
import { requestCreateLeadSource, requestDeleteLeadSource, requestUpdateLeadSource } from './request';
import { LeadConfigurationModal } from './LeadConfigurationModal';

const cols: GridColDef[] = [
  dataGridCol('numericId', {
    flex: 2,
    minWidth: 120,
  }),
  dataGridCol('name', {
    headerName: 'Source Name',
    flex: 3,
    minWidth: 120,
  }),
  dataGridCol('description', {
    flex: 3,
    minWidth: 120,
  }),
  dataGridCol('allowSecondary', {
    flex: 1,
    minWidth: 80,
    ...gridBooleanCell,
  }),
  dataGridCol('isSecondary', {
    flex: 1,
    minWidth: 80,
    ...gridBooleanCell,
  }),
  dataGridCol('isDropdownEnabled', {
    flex: 1,
    minWidth: 80,
    ...gridBooleanCell,
  }),
  dataGridCol('isAutoResponseEnabled', {
    flex: 1,
    minWidth: 100,
    ...gridBooleanCell,
  }),
  dataGridCol('autoResponsePostUrl', {
    flex: 3,
    minWidth: 120,
  }),
  dataGridCol('enableCamRef', {
    flex: 1,
    minWidth: 80,
    ...gridBooleanCell,
  }),
  dataGridCol('isAutoDeadEnabled', {
    flex: 1,
    minWidth: 80,
    ...gridBooleanCell,
  }),
  dataGridCol('autoResponseVersion', {
    flex: 1,
    minWidth: 80,
  }),
  dataGridCol('isAutoDelayedResponseEnabled', {
    flex: 1,
    minWidth: 80,
    ...gridBooleanCell,
  }),
  dataGridCol('autoDeadDelayedResponse', {
    flex: 2,
    minWidth: 100,
    ...gridNumberCell,
  }),
  dataGridCol('autoDelayedResponseOfferCallbackBaseUrl', {
    flex: 2,
    minWidth: 150,
  }),
  dataGridCol('apiKey', {
    headerName: 'XApiKey',
    flex: 1,
    minWidth: 100,
  }),
].map((field) => ({ width: 150, ...field }));

interface IPage {
  orderNumber: number;
  pageName: string;
}
export interface ILeadSourceConfiguration {
  name: string;
  source: string;
  description: string;
  isSecondary: boolean;
  allowSecondary: boolean;
  isAutoResponseEnabled: boolean;
  autoResponsePostUrl: string;
  autoResponseVersion: number;
  enableCamRef: boolean;
  isAutoDeadEnabled: boolean;
  isAutoDelayedResponseEnabled: boolean;
  autoDeadDelayedResponse: number | string;
  autoDelayedResponseOfferCallbackBaseUrl: string;
  numericId: string;
  id: string;
  customerPortalUrl: string;
  cdxPageFlows: IPage[] | [];
  thankYouPageBaseUrl: string | null;
}
export type ILeadSourceConfigurationForm = Omit<ILeadSourceConfiguration, 'cdxPageFlows' | 'thankYouPageBaseUrl'>;

export const LeadSourceConfiguration = () => {
  const { leadSourceData, reloadLeadSource } = useLeadSource<ILeadSourceConfiguration[] | []>();
  const [currentLeadConfiguration, setCurrentLeadConfiguration] = useState<null | ILeadSourceConfiguration>(null);
  const [currentLeadConfigurationPosition, setCurrentConfigurationPosition] = useState(0);
  const handleSubmit = useCallback(
    async (data: ILeadSourceConfigurationForm) => {
      const newData = {
        ...data,
        source: data.name,
        autoDeadDelayedResponse: data.autoDeadDelayedResponse || 0,
        cdxPageFlows: currentLeadConfiguration?.cdxPageFlows ?? [],
        thankYouPageBaseUrl: currentLeadConfiguration?.thankYouPageBaseUrl ?? null,
      };

      data.id ? await requestUpdateLeadSource(newData) : await requestCreateLeadSource(newData);
      reloadLeadSource();
      setCurrentLeadConfiguration(null);
      setCurrentConfigurationPosition(0);
    },
    [requestUpdateLeadSource, requestCreateLeadSource, reloadLeadSource, currentLeadConfiguration]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await requestDeleteLeadSource(id);
      reloadLeadSource();
    },
    [requestDeleteLeadSource, reloadLeadSource]
  );
  
  const handleEdit = useCallback((row:ILeadSourceConfiguration) => {
    setCurrentLeadConfiguration(row);
    setCurrentConfigurationPosition(prevState => prevState + 1);
  },[]);

  const handleClose = useCallback(() => {
    setCurrentLeadConfiguration(null);
    setCurrentConfigurationPosition(0);
  },[]);

  const handleOnButtonClick = useCallback(() => {
    setCurrentLeadConfiguration({} as ILeadSourceConfiguration);
    setCurrentConfigurationPosition(prevState => prevState + 1);
  }, []);

  const girdActions = useMemo(
    () => ({
      onEdit: (row: ILeadSourceConfiguration) => handleEdit(row),
      onDelete: (row: ILeadSourceConfiguration) => handleDelete(row.id),
    }),
    [handleDelete]
  );

  return (
    <ViewHeightWrapper>
      <PhoenixGrid
        withHeader
        canNotCopy
        disableSelectionOnClick
        rows={leadSourceData || []}
        columns={cols}
        actions={girdActions}
        pageSize={20}
        name="Lead Source"
        onButtonClick={handleOnButtonClick}
      />

      <LeadConfigurationModal
        open={!!currentLeadConfiguration}
        currentLeadConfiguration={currentLeadConfiguration}
        currentLeadConfigurationPosition={currentLeadConfigurationPosition}
        handleClose={handleClose}
        handleSubmit={handleSubmit}
      />
    </ViewHeightWrapper>
  );
};
